import { storeToRefs } from 'pinia';

import useMicSignal from '@/composables/shared/mic/process/useMicSignal';
import { useNotification } from '@/composables/shared/useNotification';
import { i18n } from '@/plugins/i18n';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call.js';
import { QRCodeType } from '@/stores/chat';
import { useMessageStore } from '@/stores/message';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import {
  InboundMessageAL,
  InboundMessageAM,
  InboundMessageAT,
  InboundMessageCA,
  InboundMessageCC,
  InboundMessageCD,
  InboundMessageCN,
  InboundMessageDM,
  InboundMessageEM,
  InboundMessageEP,
  InboundMessageER,
  InboundMessageFC,
  InboundMessageFI,
  InboundMessageFP,
  InboundMessageLD,
  InboundMessageMM,
  InboundMessageMT,
  InboundMessageSL,
  InboundMessageTR,
  InboundMessageUT,
  InboundMessageVC,
} from '@/types/socket/inbound';
import { INITIAL_MESSAGE_TEMPLATE } from '@/utils/constants/socket';
import { base64UrlSafeToString } from '@/utils/encryptor.js';
import { SocketState } from '@/utils/enums';

export const useSocketMessage = () => {
  const toastStore = useToastStore();
  const socketStore = useSocketStore();
  const microphoneStore = useMicrophoneStore();
  const messageStore = useMessageStore();
  const userStore = useUserStore();
  const audioStore = useAudioStore();
  const callStore = useCallStore();
  const micSignal = useMicSignal('MIC');
  const systemAudioMicSignal = useMicSignal('SYSTEM_AUDIO_MIC');

  const { alertAccessSimultaneous, alertSessionEnded, alertEvicted } = useNotification();

  const {
    evicted, // 강퇴 여부
    connectionResponse, // 연결 여부
    socketState, // 소켓 상태
  } = storeToRefs(socketStore);

  /**
   * [CA] Connection Accepted
   * @description 소켓 연결이 성공적으로 완료되었을 때 수신합니다.
   */
  const handleMessageCA = (msg: InboundMessageCA) => {
    const { chatId = '' } = msg;

    const userId = localStorage.getItem('userId') ?? '';
    localStorage.setItem('userIdConnected', userId);

    messageStore.patchStatus('selectedChatId', chatId);
    messageStore.patchStatus('isSocketReady', true);
    connectionResponse.value = 2;

    const now = Date.now();
    messageStore.createMessage({ chatId: now.toString(), sentAt: now }, 'DATE_MESSAGE');
  };

  /**
   * [TR] Translation Result
   * @description 번역된 텍스트/오디오 데이터를 수신합니다.
   */
  const handleMessageTR = (msg: InboundMessageTR) => {
    messageStore.upsertMessage(msg, 'MESSAGE');
  };

  /**
   * [EM] Edit Message
   * @description 수신된 메시지가 수정되었을 때 수신합니다.
   */
  const handleMessageEM = (msg: InboundMessageEM) => {
    messageStore.editMessage(msg.chatLogId, {
      dataFlag: msg.dataFlag,
      chatLogId: msg.chatLogId,
      chatSubId: msg.chatSubId,
      completeText: msg.text,
      languageCode: msg.languageCode,
    });
  };

  /**
   * [AL] Attendee List
   * @description 현재 통역에 접속 중인 전체 참여자 목록 정보를 수신합니다.
   */
  const handleMessageAL = (msg: InboundMessageAL) => {
    messageStore.patchAttendees(msg);
  };

  /**
   * [AT] Attendee Type
   * @description 통역 참여자 인원 수를 수신합니다.
   */
  const handleMessageAT = (msg: InboundMessageAT) => {
    if (messageStore.activeAttendees.length !== msg.count) {
      socketStore.sendMessageAttendees();
    }
  };

  /**
   * [CN] Change Nickname
   * @description 참여자의 닉네임이 변경되었을 때 기존 메시지 및 참여자 목록을 동기화합니다.
   */
  const handleMessageCN = (msg: InboundMessageCN) => {
    messageStore.updateMessageNicknames(msg);
  };

  /**
   * [SL] Single Language
   * @description 현재 통역방의 모든 참가자의 선택 언어가 동일할때 수신합니다.
   */
  const handleMessageSL = (msg: InboundMessageSL) => {
    const { isSingleLanguage = false } = msg;
    messageStore.patchStatus('isSingleLanguage', isSingleLanguage);
  };

  /**
   * [LD] Language Detected
   * @description 선택한 언어와 다른 발화 언어가 감지되었을 때 수신합니다.
   */
  const handleMessageLD = (msg: InboundMessageLD) => {
    messageStore.patchStatus('languageDetected', msg);
  };

  /**
   * [CD] Client Disconnected
   * @description 참가자가 통역방을 나갔을 때 수신합니다.
   */
  const handleMessageCD = (msg: InboundMessageCD) => {
    const { userId, reasonCode } = msg;
    const isMe = userId === localStorage.getItem('userIdConnected');

    // 01. 본인인 경우, ReasonCode 따른 알림 및 상태 변경합니다.
    if (isMe) {
      const alertMap: Record<string, () => void> = {
        '1': alertAccessSimultaneous, // 동시 접속
        '2': alertEvicted, // 강퇴
        '3': alertSessionEnded, // 세션 종료
      };

      const action = alertMap[reasonCode];
      evicted.value = !!action;
      if (action) {
        action();
      }
    } else {
      // 02. 타인인 경우, 시스템 메시지(퇴장 알림) 생성 여부 판단
      if (reasonCode === '1' || !callStore.qrCodeInfo.options?.showAttendee) {
        return;
      }

      // 03. 퇴장 메시지를 생성합니다.
      messageStore.createMessage(
        {
          chatLogId: msg.chatLogId,
          senderId: msg.userId,
          sentAt: msg.sentAt,
        },
        'EXIT_MESSAGE'
      );
    }
    messageStore.deleteAttendee(msg);
  };

  /**
   * [RM] Reset Microphone
   * @description AH 전송 후 3초 이내에 AS 전송하지 않을 때 수신합니다.
   */
  const handleMessageRM = async () => {
    if (microphoneStore.isMicOn) {
      await microphoneStore.startMic(microphoneStore.selectedMicrophone.id);
      socketStore.sendMessageAudioStreamStart();
    }
  };

  /**
   * [UE] Usage Exceeded
   * @description 통역 사용량 초과 시 수신합니다.
   */
  const handleMessageUE = async () => {
    messageStore.patchStatus('exceededUsageLimit', true);

    if (microphoneStore.isMicOn) {
      await microphoneStore.stopMic();
    }
    socketStore.sendMessageDisconnect();
    socketStore.close();
  };

  /**
   * [UT] Usage Time
   * @description 통역 이용 가능 시간을 수신합니다.
   */
  const handleMessageUT = (msg: InboundMessageUT) => {
    const { minutes = 0 } = msg;
    messageStore.patchStatus('remainingMinutes', minutes);
  };

  /**
   * [FP] Fixed Parameter
   * @description 통역 속도 설정값 변경 시 수신합니다.
   */
  const handleMessageFP = (msg: InboundMessageFP) => {
    const { fixedType = 'NORMAL' } = msg;
    messageStore.patchStatus('interpretationSpeed', fixedType);
  };

  /**
   * [CC] Client Connected
   * @description 새로운 참여자가 통역방에 입장했을 때 수신합니다.
   */
  const handleMessageCC = (msg: InboundMessageCC) => {
    messageStore.updateMessageNicknames({
      nickname: base64UrlSafeToString(msg.nickname),
      userId: msg.userId,
    });

    const { options: { showAttendee = false } = { showAttendee: false } } = callStore.qrCodeInfo || {};
    if (showAttendee) {
      messageStore.updateAttendee(msg);
      messageStore.createMessage(
        { chatLogId: msg.chatLogId, senderId: msg.userId, sentAt: msg.sentAt },
        'ENTER_MESSAGE'
      );
    }
  };

  /**
   * [AM] Admin Mic control
   * @description 호스트에 의해 마이크가 종료될 때 수신합니다.
   */
  const handleMessageAM = async (msg: InboundMessageAM) => {
    const { qrCodeType, callType } = callStore.qrCodeInfo || {};

    const isMuted = msg.isMuteOn === '1';
    const userId = localStorage.getItem('userIdConnected');
    const senderId = msg.senderId;

    messageStore.patchStatus('isGuestMicMuted', isMuted);
    messageStore.patchStatus('muteSenderId', isMuted ? senderId : '');

    const stopMicrophone = async (toastKey: string) => {
      if (microphoneStore.isMicOn && isMuted) {
        await microphoneStore.stopMic();
        socketStore.sendMessageAudioStreamEnd();
        toastStore.warn(i18n.global.t(toastKey));
      }
    };
    const MUTE_EVENT = {
      DEFAULT: async () => {
        if (QRCodeType.isGuest(qrCodeType)) {
          messageStore.patchStatus('isMutedByEmployee', isMuted);
          await stopMicrophone('mic_muted_by_host');
        }
      },
      MEETING: async () => {
        if (QRCodeType.isEmployee(qrCodeType) && messageStore.status.muteSenderId !== userId) {
          messageStore.patchStatus('isMutedByEmployee', isMuted);
          await stopMicrophone('mic_disabled_due_to_other_host');
        }
      },
    };
    const event = MUTE_EVENT[callType] || MUTE_EVENT.DEFAULT;
    await event();
  };

  /**
   * [FI] Fast Interpretation
   * @description 동시통역 모드 활성화/비활성화 상태를 수신합니다.
   */
  const handleMessageFI = (msg: InboundMessageFI) => {
    const { isFastInterpretationOn = '0' } = msg;
    messageStore.patchStatus('isFastInterpretation', isFastInterpretationOn === '1');
  };

  /**
   * [ER] Error
   * @description 서버와 통신중 에러 발생 시 수신합니다.
   */
  const handleMessageER = (msg: InboundMessageER) => {
    const { code = '', errorCode = 0 } = msg;
    if (code === '2002') {
      socketState.value = SocketState.ERROR_NO_HEADER;
    }
    connectionResponse.value = errorCode;
  };

  /**
   * [MT] Message Translation
   * @description DM에 대한 번역 데이터 수신 시 호출됩니다.
   */
  const handleMessageMT = (msg: InboundMessageMT) => {
    messageStore.upsertMessage(
      {
        ...INITIAL_MESSAGE_TEMPLATE,
        sentAt: Date.now(),
        dataFlag: msg.dataFlag,
        chatLogId: msg.chatLogId,
        socketId: msg.socketId,
        chatSubId: msg.chatSubId,
        languageCode: msg.languageCode,
        senderId: msg.senderId,
        completeText: msg.text,
        dmState: 'SUCCESS',
      },
      'DIRECT_MESSAGE'
    );
  };

  /**
   * [FC] Function Control / Feature Configuration
   * @description 스크린/자막 모드에서 사용되는 원격조정 옵션을 수신합니다.
   */
  const handleMessageFC = (msg: InboundMessageFC) => {
    const { loginId, languageCode, generalAttribute } = msg;

    if (!generalAttribute) {
      return;
    }

    if (loginId === userStore.userInfo.loginId) {
      audioStore.selectLanguage(languageCode);
      socketStore.sendMessageChangeLanguage(languageCode);
    }
  };

  /**
   * [EP] Extra Parameters
   * @description 마이크 상세 옵션을 수신합니다.
   */
  const handleMessageEP = (msg: InboundMessageEP) => {
    messageStore.patchStatus('micAdvancedOptions', msg);
  };

  /**
   * [VC] Voice Control
   * @description CER - 음성 상태가 변경되었을 때 수신합니다.
   */
  const handleMessageVC = (msg: InboundMessageVC) => {
    const { status = 'PENDING' } = msg;
    messageStore.patchStatus('voiceStatus', status);
  };

  /**
   * [VA] Voice Activity
   * @description CER - 사용자 음성 감지 상태 변화 시 수신합니다.
   */
  const handleMessageVA = () => {
    messageStore.patchStatus('voiceStatus', 'INITIAL');
  };

  /**
   * [TA] Timeout Alert
   * @description CER - 타임아웃으로 인해 마이크가 자동으로 꺼질 때 수신합니다.
   */
  const handleMessageTA = async () => {
    toastStore.warn(i18n.global.t('auto_mic_off'));
    await microphoneStore.stopMic();
  };

  /**
   * [DM] Delete Message
   * @description 메시지를 삭제할때 수신합니다.
   */
  const handleMessageDM = (msg: InboundMessageDM) => {
    messageStore.deleteMessage(msg.chatLogId);
  };

  /**
   * [DM] MeetingMode Microphone
   * @description 미팅모드 마이크를 컨트롤 합니다.
   */
  const handleMessageMM = (msg: InboundMessageMM) => {
    if (msg.isSystemAudio) {
      systemAudioMicSignal.handleMicSignal(msg);
    } else {
      micSignal.handleMicSignal(msg);
    }
  };

  return {
    handleMessageCA,
    handleMessageTR,
    handleMessageEM,
    handleMessageAL,
    handleMessageAT,
    handleMessageCN,
    handleMessageSL,
    handleMessageLD,
    handleMessageCD,
    handleMessageRM,
    handleMessageUE,
    handleMessageUT,
    handleMessageFP,
    handleMessageCC,
    handleMessageAM,
    handleMessageFI,
    handleMessageER,
    handleMessageMT,
    handleMessageFC,
    handleMessageEP,
    handleMessageVC,
    handleMessageVA,
    handleMessageTA,
    handleMessageDM,
    handleMessageMM,
  };
};
